import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const SYMBOLES = "!@#$%^&*()-_=+[]{};:,.<>?/|~`'\"\\";

// Miroir du validateur backend (affichage en direct seulement ; le backend reste l'autorité).
export function verifierRegle(cle, mdp, regles) {
  if (cle === 'longueur_min') return mdp.length >= regles.longueur_min;
  if (cle === 'majuscule') return /[A-ZÀ-Ý]/.test(mdp);
  if (cle === 'minuscule') return /[a-zà-ÿ]/.test(mdp);
  if (cle === 'chiffre') return /\d/.test(mdp);
  if (cle === 'symbole') return [...mdp].some((c) => SYMBOLES.includes(c));
  return true;
}

export function ListeRegles({ liste, regles, mdp }) {
  return (
    <ul className="space-y-1 text-sm" data-testid="liste-regles">
      {liste.map((r) => {
        const ok = verifierRegle(r.cle, mdp, regles);
        return (
          <li key={r.cle} className={`flex items-center gap-2 ${ok ? 'text-emerald-300' : 'text-red-300'}`} data-testid={`regle-${r.cle}`} data-ok={ok ? 'true' : 'false'}>
            {ok ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}{r.libelle}
          </li>
        );
      })}
    </ul>
  );
}

const formaterDetail = (d) => {
  if (!d) return 'Une erreur est survenue.';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  if (d.regles_manquantes) return `${d.message} ${d.regles_manquantes.join(' · ')}`;
  return d.message || String(d);
};

// 🔐 Connexion P1 : page publique de réinitialisation (token dans l'URL).
export default function ReinitialiserMotDePasse() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [etat, setEtat] = useState({ valide: null, raison: null });
  const [regles, setRegles] = useState(null);
  const [mdp, setMdp] = useState('');
  const [confirmer, setConfirmer] = useState('');
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState(false);
  const [chargement, setChargement] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/auth/reinitialiser-mot-de-passe/valider`, { params: { token } }).then((r) => setEtat(r.data)).catch(() => setEtat({ valide: false, raison: 'invalide' }));
    axios.get(`${API}/api/auth/regles-mot-de-passe`).then((r) => setRegles(r.data)).catch(() => setRegles({ regles: { longueur_min: 8, majuscule: true, chiffre: true }, liste: [] }));
  }, [token]);
  const conforme = regles && regles.liste.every((r) => verifierRegle(r.cle, mdp, regles.regles)) && mdp === confirmer && mdp.length > 0;
  const soumettre = async (e) => {
    e.preventDefault();
    setChargement(true); setErreur('');
    try {
      await axios.post(`${API}/api/auth/reinitialiser-mot-de-passe`, { token, nouveau_mot_de_passe: mdp, confirmer });
      setSucces(true);
    } catch (err) { setErreur(formaterDetail(err?.response?.data?.detail)); } finally { setChargement(false); }
  };
  const raisons = { expire: 'Ce lien de réinitialisation est expiré.', utilise: 'Ce lien de réinitialisation a déjà été utilisé.', invalide: 'Ce lien de réinitialisation est invalide.' };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b1437] p-4" data-testid="page-reinitialiser-mot-de-passe">
      <div className="w-full max-w-md bg-white/5 border border-white/10 rounded-2xl p-8 text-slate-100 space-y-4">
        <h1 className="text-xl font-semibold">Nouveau mot de passe</h1>
        {etat.valide === null && <p className="text-sm text-slate-300" data-testid="reinit-verification">Vérification du lien…</p>}
        {etat.valide === false && (
          <div className="space-y-4" data-testid="reinit-lien-invalide">
            <p className="text-sm text-red-300">{raisons[etat.raison] || raisons.invalide} Demandez un nouveau lien pour continuer.</p>
            <Button asChild className="w-full" data-testid="reinit-demander-nouveau"><Link to="/mot-de-passe-oublie">Demander un nouveau lien</Link></Button>
          </div>
        )}
        {etat.valide && succes && (
          <div className="space-y-4" data-testid="reinit-succes">
            <p className="text-sm text-emerald-300">Votre mot de passe a été modifié. Vous pouvez maintenant vous connecter.</p>
            <Button asChild className="w-full" data-testid="reinit-se-connecter"><Link to="/login">Se connecter</Link></Button>
          </div>
        )}
        {etat.valide && !succes && (
          <form onSubmit={soumettre} className="space-y-4" data-testid="reinit-formulaire">
            <Input type="password" value={mdp} onChange={(e) => setMdp(e.target.value)} placeholder="Nouveau mot de passe" className="bg-white/10 border-white/20 text-white" data-testid="reinit-mdp" />
            <Input type="password" value={confirmer} onChange={(e) => setConfirmer(e.target.value)} placeholder="Confirmer le mot de passe" className="bg-white/10 border-white/20 text-white" data-testid="reinit-confirmer" />
            {regles && <ListeRegles liste={regles.liste} regles={regles.regles} mdp={mdp} />}
            {confirmer && mdp !== confirmer && <p className="text-sm text-red-300" data-testid="reinit-non-identiques">Les mots de passe ne sont pas identiques.</p>}
            {erreur && <p className="text-sm text-red-300" data-testid="reinit-erreur">{erreur}</p>}
            <Button type="submit" className="w-full" disabled={!conforme || chargement} data-testid="reinit-soumettre">{chargement ? 'Enregistrement…' : 'Enregistrer le nouveau mot de passe'}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
